/**
 * Lifecycle owner for the background-effect pipeline.
 *
 * `apply` is async (the segmenter has to load) and the user can flip the effect
 * three times while it is in flight, so every call takes a generation number
 * and a stale one throws its work away. Without that, a fast toggle leaves two
 * compositors rendering into two canvases and the camera light stays on after
 * the effect is turned off.
 */
import {
  IDLE_VIDEO_EFFECT_RUNTIME,
  type VideoEffectPreferences,
  type VideoEffectRuntime,
} from '../settings/preferences';
import { createVideoEffectPipeline, type VideoEffectPipeline } from './effectPipeline';
import { resolveVideoEffect } from './effectResolver';

export interface EffectController {
  /** The processed track, or null when the raw camera track should be used. */
  readonly track: MediaStreamTrack | null;
  readonly status: VideoEffectRuntime;
  apply(source: MediaStreamTrack | null, preferences: VideoEffectPreferences): Promise<void>;
  stop(): void;
}

export function createEffectController(onChange: () => void): EffectController {
  let pipeline: VideoEffectPipeline | null = null;
  let status: VideoEffectRuntime = IDLE_VIDEO_EFFECT_RUNTIME;
  let generation = 0;

  /**
   * The pipeline reports once a second. Publishing every one of those would
   * re-render the whole meeting tree 60 times a minute for a number almost
   * nobody is looking at, so a frame-rate wobble under this many fps is not a
   * change — anything the user can act on still is.
   */
  const FPS_NOISE = 2;

  const setStatus = (next: VideoEffectRuntime): void => {
    if (
      next.kind === status.kind &&
      next.active === status.active &&
      next.degraded === status.degraded &&
      next.reason === status.reason &&
      Math.abs(next.fps - status.fps) <= FPS_NOISE
    ) {
      return;
    }
    status = next;
    onChange();
  };

  const teardown = (): void => {
    pipeline?.stop();
    pipeline = null;
  };

  return {
    get track() {
      return pipeline?.track ?? null;
    },
    get status() {
      return status;
    },

    async apply(source, preferences) {
      generation += 1;
      const mine = generation;
      teardown();

      if (!source || preferences.kind === 'none') {
        setStatus(IDLE_VIDEO_EFFECT_RUNTIME);
        return;
      }

      const resolution = await resolveVideoEffect(preferences);
      if (mine !== generation) return;

      if (!resolution.ok) {
        setStatus(resolution.runtime);
        return;
      }

      const { effect, segmenter } = resolution.value;
      pipeline = createVideoEffectPipeline(source, effect, segmenter, (runtime) => {
        if (mine === generation) setStatus(runtime);
      });
      setStatus({ kind: preferences.kind, active: true, degraded: false, reason: null, fps: 0 });
    },

    stop() {
      generation += 1;
      teardown();
      setStatus(IDLE_VIDEO_EFFECT_RUNTIME);
    },
  };
}
